import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PrivateLayout from "../../layouts/PrivateLayout";
import { fetchCurrentRaidTeam, fetchRaidReports } from "../../api/api";
import useDecodedToken from "../../utils/useDecodedToken";
import { formatDateTime, getRoleIcon, getRoleColor } from "./raidStatsUtils";

const REPORTS_PAGE_SIZE = 15;

function RaidStatistics() {
  const backgroundImage = "/ww-wallpaper.webp";
  const navigate = useNavigate();
  const { token } = useDecodedToken();

  const [team, setTeam] = useState(null);
  const [teamError, setTeamError] = useState("");
  const [isLoadingTeam, setIsLoadingTeam] = useState(false);

  const [reports, setReports] = useState([]);
  const [totalReports, setTotalReports] = useState(0);
  const [offset, setOffset] = useState(0);
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const [reportsError, setReportsError] = useState("");

  const loadTeam = useCallback(async () => {
    if (!token) return;
    setIsLoadingTeam(true);
    setTeamError("");
    try {
      const data = await fetchCurrentRaidTeam(token);
      setTeam(data);
    } catch (error) {
      if (error.status === 404) {
        setTeam(null);
      } else {
        setTeamError(error.message || "Failed to load raid team.");
      }
    } finally {
      setIsLoadingTeam(false);
    }
  }, [token]);

  const loadReports = useCallback(async () => {
    if (!token) return;
    setIsLoadingReports(true);
    setReportsError("");
    try {
      const data = await fetchRaidReports(token, {
        limit: REPORTS_PAGE_SIZE,
        offset,
      });
      setReports(data?.reports || []);
      setTotalReports(data?.total || 0);
    } catch (error) {
      setReportsError(error.message || "Failed to load raid reports.");
    } finally {
      setIsLoadingReports(false);
    }
  }, [token, offset]);

  useEffect(() => {
    loadTeam();
  }, [loadTeam]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const hasNextPage = offset + REPORTS_PAGE_SIZE < totalReports;
  const hasPrevPage = offset > 0;

  return (
    <PrivateLayout backgroundImage={backgroundImage}>
      <div className="flex flex-col items-center text-white bg-black/50 px-4 py-10 min-h-full">
        <div className="w-full max-w-6xl">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-5xl font-bold drop-shadow-lg mb-2">
              Raid Statistics
            </h1>
            <p className="text-gray-300 text-base md:text-lg">
              WarcraftLogs performance data for our raid team
            </p>
          </div>

          {/* Current Roster */}
          <div className="mb-10">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <i className="fas fa-users text-blue-400"></i>
              Current Roster
              {team?.season && (
                <span className="text-sm font-normal text-gray-400">
                  ({team.season})
                </span>
              )}
            </h2>

            {isLoadingTeam && (
              <div className="flex items-center justify-center py-10">
                <div className="w-8 h-8 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {!isLoadingTeam && teamError && (
              <div className="bg-red-500/20 border border-red-500/40 rounded-lg px-4 py-3 text-red-300 text-sm text-center">
                {teamError}
              </div>
            )}

            {!isLoadingTeam && !teamError && !team && (
              <div className="text-center py-10 bg-white/5 border border-white/10 rounded-xl">
                <i className="fas fa-shield-halved text-4xl text-gray-600 mb-3 block"></i>
                <p className="text-gray-400">No raid team configured yet.</p>
              </div>
            )}

            {!isLoadingTeam && !teamError && team && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {team.members?.length === 0 && (
                  <p className="text-gray-400 col-span-full text-center py-6">
                    No members on this roster yet.
                  </p>
                )}
                {team.members?.map((member) => (
                  <button
                    type="button"
                    key={member.id}
                    onClick={() =>
                      navigate(`/raid-statistics/characters/${member.characterId}`)
                    }
                    className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2.5 text-left transition-colors"
                  >
                    <i
                      className={`${getRoleIcon(member.role)} text-sm flex-shrink-0 ${getRoleColor(member.role)}`}
                      title={member.role}
                    ></i>
                    <div className="min-w-0">
                      <p className="font-semibold text-sm truncate text-white">
                        {member.characterName}
                      </p>
                      <p className="text-xs text-gray-400 truncate">
                        {member.realm}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Raid Reports */}
          <div>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <i className="fas fa-scroll text-blue-400"></i>
              Raid Reports
            </h2>

            {reportsError && (
              <div className="mb-4 bg-red-500/20 border border-red-500/40 rounded-lg px-4 py-3 text-red-300 text-sm text-center">
                {reportsError}
              </div>
            )}

            {isLoadingReports && (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {!isLoadingReports && !reportsError && reports.length === 0 && (
              <div className="text-center py-16 bg-white/5 border border-white/10 rounded-xl">
                <i className="fas fa-scroll text-4xl text-gray-600 mb-3 block"></i>
                <p className="text-gray-400">No raid reports found yet.</p>
              </div>
            )}

            {!isLoadingReports && !reportsError && reports.length > 0 && (
              <div className="rounded-xl overflow-hidden border border-white/10 shadow-2xl">
                <div className="divide-y divide-white/5">
                  {reports.map((report) => (
                    <button
                      type="button"
                      key={report.code}
                      onClick={() => navigate(`/raid-statistics/reports/${report.code}`)}
                      className="w-full flex flex-col md:flex-row md:items-center md:justify-between gap-2 px-4 py-3.5 bg-white/5 hover:bg-white/10 text-left transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="font-semibold text-sm truncate">
                          {report.title || report.code}
                        </p>
                        <p className="text-xs text-gray-400 truncate">
                          {report.zoneName} &middot; {formatDateTime(report.startTime)}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 font-medium">
                          {report.kills} kills
                        </span>
                        <span className="text-xs px-2.5 py-1 rounded-full bg-red-500/20 text-red-300 font-medium">
                          {report.wipes} wipes
                        </span>
                        <i className="fas fa-chevron-right text-gray-500 text-xs"></i>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="bg-white/10 px-4 py-3 flex items-center justify-between text-sm">
                  <button
                    type="button"
                    disabled={!hasPrevPage}
                    onClick={() =>
                      setOffset((prev) => Math.max(0, prev - REPORTS_PAGE_SIZE))
                    }
                    className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <i className="fas fa-chevron-left mr-1"></i>
                    Prev
                  </button>
                  <span className="text-gray-400">
                    {totalReports === 0
                      ? "0 reports"
                      : `${offset + 1}-${Math.min(offset + REPORTS_PAGE_SIZE, totalReports)} of ${totalReports}`}
                  </span>
                  <button
                    type="button"
                    disabled={!hasNextPage}
                    onClick={() => setOffset((prev) => prev + REPORTS_PAGE_SIZE)}
                    className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                    <i className="fas fa-chevron-right ml-1"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </PrivateLayout>
  );
}

export default RaidStatistics;
